/**
 * Time — the standalone tracker's Insights screen, on Project Hub's data.
 *
 * Same questions in the same order: how much, how much of it was billable,
 * what it was worth, how it fell across the days, and which projects it went
 * to. The two differences are deliberate:
 *
 *  - The value figure comes from each entry's `rateSnapshot`, not the project's
 *    current rate. The tracker multiplies old hours by today's rate, so a rate
 *    rise silently rewrites last year; here a rate change applies to new
 *    entries only, and a past quarter reports what it reported at the time.
 *  - Clicking a project opens the entries behind it rather than filtering in
 *    place, because those entries live on the Time screen and duplicating the
 *    list here would be a second place to correct them from.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ExportPanel } from "@/components/ExportPanel";
import { InvoiceBuilder } from "./InvoiceBuilder";
import { HoursChart } from "@/components/HoursChart";
import { dayKey } from "@/components/PeriodPicker";
import { card } from "@/components/atoms";
import { useProjects } from "@/hooks/useData";
import { useClients, useTimeEntries } from "@/hooks/useTime";
import {
  clientNameOf, groupBy, hours, inBounds, money, periodBounds, projectName,
  totalsOf, type Period,
} from "@/lib/timeRollup";
import { useIsMobile } from "@/lib/useMediaQuery";

import { InvoiceExport } from "./InvoiceExport";
import { Breakdown, PeriodPager, Stat, ghostBtn } from "./parts";

/**
 * Week, month, quarter — the tracker's set.
 *
 * Quarter was taken out of Project Hub's period controls so Reports, Time and
 * the client report all offered the same three. It is back here alone, because
 * a quarter is the unit this screen is actually read on and the other two are
 * not asking the same question.
 */
const PERIODS: [Period, string][] = [["week", "Week"], ["month", "Month"], ["quarter", "Quarter"]];

export function TimeReport() {
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const mobile = useIsMobile();
  const navigate = useNavigate();

  const [activity, setActivity] = useState("all");
  const [period, setPeriod] = useState<Period>("week");
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [building, setBuilding] = useState(false);

  const bounds = useMemo(() => periodBounds(period, offset), [period, offset]);
  /** Days with time logged, dotted in the period picker. */
  const loggedDays = useMemo(() => new Set(entries.map((e) => dayKey(new Date(e.start)))), [entries]);

  /**
   * Offered from the data, not from the configured list.
   *
   * An activity you have since deleted from Settings is still attached to the
   * entries you logged under it, and those are exactly the ones you would go
   * looking for. A filter built from the settings list would silently hide
   * them.
   */
  const activityOptions = useMemo(() => {
    const seen = new Set(entries.map((e) => e.category).filter(Boolean) as string[]);
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [entries]);

  const shown = useMemo(() => entries.filter((e) => {
    if (!inBounds(e, bounds)) return false;
    if (activity === "all") return true;
    if (activity === "none") return !e.category;
    return e.category === activity;
  }), [entries, bounds, activity]);
  const totals = useMemo(() => totalsOf(shown, projects), [shown, projects]);

  /** Days with something on them — an average over idle days is not a rate. */
  const activeDays = useMemo(
    () => new Set(shown.map((e) => new Date(e.start).toDateString())).size,
    [shown],
  );
  // Client work is everything done for a client, billed by the hour or not.
  const clientPct = totals.seconds > 0
    ? Math.round((totals.clientSeconds / totals.seconds) * 100)
    : 0;

  const byProject = useMemo(
    () => [...groupBy(shown.filter((e) => e.project), (e) => e.project!)]
      .map(([id, rows]) => ({ id, label: projectName(projects, id), totals: totalsOf(rows, projects) }))
      .sort((a, b) => b.totals.seconds - a.totals.seconds),
    [shown, projects],
  );

  const byClient = useMemo(
    () => [...groupBy(shown.filter((e) => e.client), (e) => e.client!)]
      .map(([id, rows]) => ({ id, label: clientNameOf(clients, id) ?? "—", totals: totalsOf(rows, projects) }))
      .sort((a, b) => b.totals.seconds - a.totals.seconds),
    [shown, clients, projects],
  );

  /**
   * By activity — across everything, not just ad-hoc time. The unlabelled
   * remainder is shown rather than hidden, so the rows sum to the total above.
   */
  const byActivity = useMemo(() => {
    if (!shown.some((e) => e.category)) return [];
    return [...groupBy(shown, (e) => e.category || "No activity")]
      .map(([label, rows]) => ({ label, totals: totalsOf(rows, projects) }))
      .sort((a, b) => b.totals.seconds - a.totals.seconds);
  }, [shown, projects]);

  // The entries behind a row, for the same period this report is showing, so
  // the total there is the total here.
  const openEntries = (filter: string) =>
    navigate(`/time?tab=entries&${filter}&period=${period}&offset=${offset}`);
  const openEntriesFor = (projectId: string) => openEntries(`project=${encodeURIComponent(projectId)}`);
  const openEntriesForClient = (clientId: string) => openEntries(`client=${encodeURIComponent(clientId)}`);
  const openEntriesForActivity = (label: string) =>
    openEntries(`activity=${encodeURIComponent(label === "No activity" ? "none" : label)}`);

  return (
    <>
      <PeriodPager
        periods={PERIODS}
        period={period}
        offset={offset}
        bounds={bounds}
        onPeriod={setPeriod}
        onOffset={setOffset}
        marked={loggedDays}
        trailing={
          <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {activityOptions.length > 0 && (
              <select
                value={activity}
                onChange={(e) => setActivity(e.target.value)}
                aria-label="Filter by activity"
                style={{
                  ...ghostBtn, cursor: "pointer", maxWidth: 190,
                  ...(activity !== "all"
                    ? { background: "var(--accentTint)", borderColor: "var(--accentLine)", color: "var(--accent)" }
                    : null),
                }}
              >
                <option value="all">All activities</option>
                <option value="none">No activity</option>
                {activityOptions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            <button onClick={() => setBuilding(true)} style={ghostBtn}>Invoice builder…</button>
            <button onClick={() => setInvoicing(true)} style={ghostBtn}>Invoice attachment…</button>
            <button onClick={() => setExporting(true)} style={ghostBtn}>Export…</button>
          </span>
        }
      />

      <div style={{ ...card, padding: 18, display: "flex", gap: 26, flexWrap: "wrap", alignItems: "baseline" }}>
        <Stat label="Logged" value={`${hours(totals.seconds)} h`} />
        <Stat label="Billable" value={`${hours(totals.billableSeconds)} h`} />
        <Stat label="Project work" value={`${hours(totals.projectSeconds)} h`} />
        {totals.noChargeSeconds > 0 && <Stat label="No charge" value={`${hours(totals.noChargeSeconds)} h`} />}
        <Stat label="Internal" value={`${hours(totals.internalSeconds)} h`} />
        <Stat label="Client work" value={totals.seconds > 0 ? `${clientPct}%` : "—"} />
        <Stat
          label="Average a day"
          value={activeDays > 0 ? `${hours(totals.seconds / activeDays)} h` : "—"}
        />
        <Stat label="Amount" value={totals.amount > 0 ? money(totals.amount, totals.currency) : "—"} />
      </div>

      {/* Directly under the stats, because it answers what they raise. */}
      {shown.length > 0 && (
        <div style={{ ...card, padding: 18, marginTop: 14 }}>
          <HoursChart entries={shown} projects={projects} period={period} offset={offset} mobile={mobile} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 14, marginTop: 14 }}>
        <Breakdown
          title="By project"
          rows={byProject}
          hint="Click a project to open its entries."
          onRow={(row) => row.id && openEntriesFor(row.id)}
          colorOf={(row) => projects.find((p) => p.id === row.id)?.color}
        />
        <Breakdown
          title="By client"
          rows={byClient}
          hint="Click a client to open its entries."
          onRow={(row) => row.id && openEntriesForClient(row.id)}
        />
      </div>

      {byActivity.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Breakdown
            title="By activity"
            rows={byActivity}
            hint="What the time was, regardless of which project it sat on. Click one to open its entries; set the list in Settings › Activities."
            onRow={(row) => openEntriesForActivity(row.label)}
          />
        </div>
      )}

      {invoicing && <InvoiceExport onClose={() => setInvoicing(false)} />}
      {building && <InvoiceBuilder onClose={() => setBuilding(false)} />}

      {exporting && (
        <ExportPanel
          entries={shown}
          projects={projects}
          clients={clients}
          period={period}
          offset={offset}
          onClose={() => setExporting(false)}
        />
      )}
    </>
  );
}
